import { useState, useEffect } from"react";
import { useOpenFromQuery } from "@/hooks/useOpenFromQuery";
import axios from"axios";
import { useTranslation } from"react-i18next";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Input } from"@/components/ui/input";
import { Label } from"@/components/ui/label";
import { Textarea } from"@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from"@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { Badge } from"@/components/ui/badge";
import { Plus, ClipboardList } from"lucide-react";
import { toast } from"sonner";

const TasksPage = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [tasks, setTasks] = useState([]);
 const [units, setUnits] = useState([]);
 const [trades, setTrades] = useState([]);
 const [users, setUsers] = useState([]);
 const [engineers, setEngineers] = useState([]);
 const [contractors, setContractors] = useState([]);
 const [projects, setProjects] = useState([]);
 const [buildings, setBuildings] = useState([]);
 const [loading, setLoading] = useState(true);
 const [dialogOpen, setDialogOpen] = useState(false);
 const [detailOpen, setDetailOpen] = useState(false);
 const [selectedTask, setSelectedTask] = useState(null);
 const [assignToAllUnits, setAssignToAllUnits] = useState(false);
 const [editMode, setEditMode] = useState(false);
 const [editedTask, setEditedTask] = useState(null);
 const [newMessage, setNewMessage] = useState("");
 const [selectedProjectForTask, setSelectedProjectForTask] = useState("");
 const [formData, setFormData] = useState({
 unit_id:"",
 trade_id:"",
 name:"",
 description:"",
 assigned_to:""
 });

 useEffect(() => {
 loadData();
 }, []);

 const loadData = async () => {
 try {
 const promises = [
 axios.get("/tasks"),
 axios.get("/units"),
 axios.get("/trades"),
 axios.get("/projects"),
 axios.get("/buildings")
 ];
 
 // Load users for all roles (contractors need to see their own name and engineer names)
 if (["owner","management","engineer","contractor"].includes(user.role)) {
 promises.push(axios.get("/users"));
 }

 const responses = await Promise.all(promises);
 setTasks(responses[0].data);
 setUnits(responses[1].data);
 setTrades(responses[2].data);
 setProjects(responses[3].data);
 setBuildings(responses[4].data);
 if (responses[5]) {
 const allUsers = responses[5].data;
 setUsers(allUsers);
 setContractors(allUsers.filter(u => u.role ==="contractor"));
 setEngineers(allUsers.filter(u => u.role ==="engineer"));
 }
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 } finally {
 setLoading(false);
 }
 };

 const openTaskDetail = (task) => {
 setSelectedTask(task);
 setEditedTask({...task});
 setEditMode(false);
 setNewMessage("");
 setDetailOpen(true);
 };
 useOpenFromQuery("taskId", tasks, openTaskDetail);

 const handleUpdateTask = async () => {
 try {
 await axios.patch(`/tasks/${selectedTask.id}`, {
 assigned_to: editedTask.assigned_to,
 assigned_engineer: editedTask.assigned_engineer
 });
 toast.success(t("messages.saveSuccess"));
 setEditMode(false);
 loadData();
 setDetailOpen(false);
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Failed to update task");
 }
 };

 const handleAddMessage = async () => {
 if (!newMessage.trim()) return;
 
 try {
 const messages = selectedTask.messages || [];
 messages.push({
 text: newMessage,
 sender: user.name,
 sender_id: user.id,
 timestamp: new Date().toISOString()
 });
 
 await axios.patch(`/tasks/${selectedTask.id}`, { messages });
 toast.success(t("messages.saveSuccess"));
 setNewMessage("");
 setSelectedTask({...selectedTask, messages});
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 try {
 if (assignToAllUnits) {
 // Get the project for the selected unit
 const selectedUnit = units.find(u => u.id === formData.unit_id);
 if (!selectedUnit) {
 toast.error(t("validation.required"));
 return;
 }
 
 const building = buildings.find(b => b.id === selectedUnit.building_id);
 if (!building) {
 toast.error(t("messages.loadFailed"));
 return;
 }
 
 // Get all units in the same project
 const projectUnits = units.filter(u => {
 const unitBuilding = buildings.find(b => b.id === u.building_id);
 return unitBuilding && unitBuilding.project_id === building.project_id;
 });
 
 // Create task for each unit in the project
 const taskPromises = projectUnits.map(unit => 
 axios.post("/tasks", {
 unit_id: unit.id,
 trade_id: formData.trade_id,
 name: formData.name,
 description: formData.description,
 assigned_to: formData.assigned_to
 })
 );
 
 await Promise.all(taskPromises);
 toast.success(`Created ${projectUnits.length} tasks for all units in the project`);
 } else {
 // Create single task
 await axios.post("/tasks", formData);
 toast.success(t("messages.saveSuccess"));
 }
 
 setDialogOpen(false);
 setFormData({ unit_id:"", trade_id:"", name:"", description:"", assigned_to:""});
 setAssignToAllUnits(false);
 loadData();
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Failed to create task");
 }
 };

 const statusColors = {
 pending:"bg-white/60 text-slate-800 border-slate-200",
 in_progress:"bg-blue-500/10 text-blue-700 border-blue-200/60",
 completed:"bg-emerald-500/10 text-emerald-700 border-green-200/60"
 };

 return (
 <Layout user={user} onLogout={onLogout}>
 <div data-testid="tasks-page"className="space-y-6">
 <div className="flex items-center justify-between">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900 mb-1">{t("tasks.title")}</h1>
 <p className="text-slate-600">Manage construction tasks</p>
 </div>
 {["owner","management","engineer"].includes(user.role) && (
 <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
 <DialogTrigger asChild>
 <Button data-testid="create-task-button"className="bg-slate-900 hover:bg-slate-800:bg-zinc-200">
 <Plus className="w-4 h-4 mr-2"/>
 Create Task
 </Button>
 </DialogTrigger>
 <DialogContent data-testid="create-task-dialog">
 <DialogHeader>
 <DialogTitle>Create New Task</DialogTitle>
 </DialogHeader>
 <form onSubmit={handleSubmit} className="space-y-4">
 <div>
 <Label htmlFor="project_id">Project</Label>
 <Select value={selectedProjectForTask} onValueChange={(val) => {
 setSelectedProjectForTask(val);
 setFormData({ ...formData, unit_id:""});
 }}>
 <SelectTrigger>
 <SelectValue placeholder="Select project first"/>
 </SelectTrigger>
 <SelectContent>
 {projects.map((project) => (
 <SelectItem key={project.id} value={project.id}>
 {project.name}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label htmlFor="unit_id">Unit</Label>
 <Select 
 value={formData.unit_id} 
 onValueChange={(val) => setFormData({ ...formData, unit_id: val })}
 disabled={!selectedProjectForTask}
 >
 <SelectTrigger data-testid="task-unit-select">
 <SelectValue placeholder={selectedProjectForTask ?"Select unit":"Select project first"} />
 </SelectTrigger>
 <SelectContent>
 {units
 .filter(unit => {
 const building = buildings.find(b => b.id === unit.building_id);
 return building && building.project_id === selectedProjectForTask;
 })
 .map((unit) => (
 <SelectItem key={unit.id} value={unit.id}>
 {unit.code}
 </SelectItem>
 ))
 }
 </SelectContent>
 </Select>
 {selectedProjectForTask && units.filter(u => {
 const building = buildings.find(b => b.id === u.building_id);
 return building && building.project_id === selectedProjectForTask;
 }).length === 0 && (
 <p className="text-sm text-orange-600 mt-1">No units in this project. Create units first.</p>
 )}
 </div>
 <div className="flex items-center space-x-2 p-3 bg-blue-50/80 border border-blue-200/60 rounded-sm">
 <input
 type="checkbox"
 id="assign-all-units"
 checked={assignToAllUnits}
 onChange={(e) => setAssignToAllUnits(e.target.checked)}
 className="w-4 h-4 text-blue-600 border-slate-200 rounded focus:ring-blue-500"
 />
 <Label htmlFor="assign-all-units"className="cursor-pointer text-sm font-medium text-blue-900">
 Assign this task to all units in the project
 </Label>
 </div>
 <div>
 <Label htmlFor="trade_id">Trade</Label>
 <Select value={formData.trade_id} onValueChange={(val) => setFormData({ ...formData, trade_id: val })}>
 <SelectTrigger data-testid="task-trade-select">
 <SelectValue placeholder="Select trade"/>
 </SelectTrigger>
 <SelectContent>
 {trades.map((trade) => (
 <SelectItem key={trade.id} value={trade.id}>
 {trade.name}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label htmlFor="name">Task Name</Label>
 <Input
 id="name"
 data-testid="task-name-input"
 value={formData.name}
 onChange={(e) => setFormData({ ...formData, name: e.target.value })}
 required
 />
 </div>
 <div>
 <Label htmlFor="description">{t("common.description")}</Label>
 <Textarea
 id="description"
 data-testid="task-description-input"
 value={formData.description}
 onChange={(e) => setFormData({ ...formData, description: e.target.value })}
 rows={3}
 />
 </div>
 <div>
 <Label htmlFor="assigned_to">Assign to Contractor</Label>
 <Select value={formData.assigned_to} onValueChange={(val) => setFormData({ ...formData, assigned_to: val })}>
 <SelectTrigger data-testid="task-assigned-select">
 <SelectValue placeholder="Select contractor"/>
 </SelectTrigger>
 <SelectContent>
 {users.map((u) => (
 <SelectItem key={u.id} value={u.id}>
 {u.name}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 <Button type="submit"data-testid="submit-task-button"className="w-full bg-slate-900 hover:bg-slate-800:bg-zinc-200">
 Create Task
 </Button>
 </form>
 </DialogContent>
 </Dialog>
 )}
 </div>

 {loading ? (
 <div className="flex items-center justify-center h-64">
 <div className="text-slate-600">Loading tasks...</div>
 </div>
 ) : (
 <div className="grid grid-cols-1 gap-4">
 {tasks.map((task) => (
 <Card 
 key={task.id} 
 data-testid={`task-card-${task.id}`} 
 className="border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
 onClick={() => openTaskDetail(task)}
 >
 <CardContent className="p-4">
 <div className="flex items-start justify-between">
 <div className="flex items-start gap-3 flex-1">
 <ClipboardList className="w-6 h-6 text-blue-600 mt-1"/>
 <div className="flex-1">
 <h3 className="font-medium text-slate-900 mb-1">{task.name}</h3>
 {task.description && (
 <p className="text-sm text-slate-600 mb-2">{task.description}</p>
 )}
 <div className="flex items-center gap-4 text-[11px] text-slate-500">
 <span>Unit: {units.find(u => u.id === task.unit_id)?.code ||"Unknown"}</span>
 <span>Trade: {trades.find(t => t.id === task.trade_id)?.name ||"Unknown"}</span>
 {task.assigned_to && <span>Assigned: {contractors.find(c => c.id === task.assigned_to)?.name || users.find(usr => usr.id === task.assigned_to)?.name ||"Unknown"}</span>}
 </div>
 </div>
 </div>
 <div className="flex flex-col gap-2 items-end">
 <Badge variant="outline"className={statusColors[task.status]}>
 {task.status.replace('_', ' ')}
 </Badge>
 {(user.role ==="owner"|| user.role ==="management"|| user.role ==="engineer") && (
 <Button
 onClick={async (e) => {
 e.stopPropagation();
 if (window.confirm("Are you sure you want to delete this task?")) {
 try {
 await axios.delete(`/tasks/${task.id}`);
 toast.success(t("messages.saveSuccess"));
 loadData();
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Failed to delete task");
 }
 }
 }}
 variant="destructive"
 size="sm"
 type="button"
 >
 Delete
 </Button>
 )}
 </div>
 </div>
 </CardContent>
 </Card>
 ))}
 </div>
 )}

 {/* Task Detail Modal */}
 <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
 <DialogContent className="max-w-2xl">
 <DialogHeader>
 <DialogTitle>Task Details</DialogTitle>
 </DialogHeader>
 {selectedTask && (
 <div className="space-y-6">
 {/* Task Info */}
 <div className="space-y-3">
 <div>
 <Label className="text-sm font-medium text-[#334155]">Task Name</Label>
 <p className="text-base font-semibold text-slate-900">{selectedTask.name}</p>
 </div>
 <div>
 <Label className="text-sm font-medium text-[#334155]">Description</Label>
 <p className="text-sm text-slate-500">{selectedTask.description ||"No description"}</p>
 </div>
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label className="text-sm font-medium text-[#334155]">Status</Label>
 <Badge variant="outline"className={`${statusColors[selectedTask.status]} mt-1`}>
 {selectedTask.status.replace('_', ' ')}
 </Badge>
 </div>
 <div>
 <Label className="text-sm font-medium text-[#334155]">Unit</Label>
 <p className="text-sm text-slate-500">{units.find(u => u.id === selectedTask.unit_id)?.code ||"Unknown"}</p>
 </div>
 </div>
 </div>

 {/* Assignments Section */}
 <div className="border-t pt-4">
 <Label className="text-sm font-bold text-slate-900 mb-3 block">Assignments</Label>
 <div className="space-y-3">
 {editMode ? (
 <>
 <div>
 <Label>Assigned Contractor</Label>
 <Select 
 value={editedTask.assigned_to ||""} 
 onValueChange={(val) => setEditedTask({...editedTask, assigned_to: val})}
 >
 <SelectTrigger>
 <SelectValue placeholder="Select contractor"/>
 </SelectTrigger>
 <SelectContent>
 {contractors.map((c) => (
 <SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label>Assigned Engineer</Label>
 <Select 
 value={editedTask.assigned_engineer ||"unassigned"} 
 onValueChange={(val) => setEditedTask({...editedTask, assigned_engineer: val ==="unassigned"? null : val})}
 >
 <SelectTrigger>
 <SelectValue placeholder="Select engineer (optional)"/>
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="unassigned">None (Unassigned)</SelectItem>
 {engineers.map((e) => (
 <SelectItem key={e.id} value={e.id}>{e.name} ({e.email})</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 </>
 ) : (
 <>
 <div className="flex items-center justify-between p-3 bg-white/50 rounded-sm">
 <div>
 <Label className="text-xs text-slate-600">Contractor</Label>
 <p className="text-sm font-medium text-slate-900">
 {contractors.find(c => c.id === selectedTask.assigned_to)?.name ||"Not assigned"}
 </p>
 </div>
 </div>
 <div className="flex items-center justify-between p-3 bg-white/50 rounded-sm">
 <div>
 <Label className="text-xs text-slate-600">Engineer</Label>
 <p className="text-sm font-medium text-slate-900">
 {engineers.find(e => e.id === selectedTask.assigned_engineer)?.name ||"Not assigned"}
 </p>
 </div>
 </div>
 </>
 )}
 </div>
 {(user.role ==="owner"|| user.role ==="management") && (
 <div className="mt-3 flex gap-2">
 {editMode ? (
 <>
 <Button onClick={handleUpdateTask} className="flex-1">Save Changes</Button>
 <Button onClick={() => setEditMode(false)} variant="outline"className="flex-1">Cancel</Button>
 </>
 ) : (
 <Button onClick={() => setEditMode(true)} variant="outline"className="w-full">Edit Assignments</Button>
 )}
 </div>
 )}
 </div>

 {/* Messages Section */}
 <div className="border-t pt-4">
 <Label className="text-sm font-bold text-slate-900 mb-3 block">Messages & Notes</Label>
 <div className="space-y-3 max-h-60 overflow-y-auto mb-3">
 {selectedTask.messages && selectedTask.messages.length > 0 ? (
 selectedTask.messages.map((msg, idx) => (
 <div key={idx} className="p-3 bg-blue-50/80 rounded-sm">
 <div className="flex items-center justify-between mb-1">
 <span className="text-xs font-semibold text-blue-900">{msg.sender}</span>
 <span className="text-[11px] text-slate-500">
 {new Date(msg.timestamp).toLocaleString()}
 </span>
 </div>
 <p className="text-sm text-[#334155]">{msg.text}</p>
 </div>
 ))
 ) : (
 <p className="text-sm text-slate-500 italic">No messages yet</p>
 )}
 </div>
 <div className="flex gap-2">
 <Textarea
 value={newMessage}
 onChange={(e) => setNewMessage(e.target.value)}
 placeholder="Add a message..."
 rows={2}
 className="flex-1"
 />
 <Button onClick={handleAddMessage} className="self-end">Send</Button>
 </div>
 </div>
 </div>
 )}
 </DialogContent>
 </Dialog>
 </div>
 </Layout>
 );
};

export default TasksPage;
